import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Alert,
  Fade,
  Grid,
} from '@mui/material';
import { People, PersonAdd } from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';
import DataTable from '../components/common/DataTable';
import FormDialog from '../components/common/FormDialog';
import ConfirmDialog from '../components/common/ConfirmDialog';
import SearchInput from '../components/common/SearchInput';
import api from '../services/api';

const initialFormData = {
  name: '',
  email: '',
  password: '',
};

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (err) {
      setError('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
      });
      setSelectedUser(user);
    } else {
      setFormData(initialFormData);
      setSelectedUser(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(initialFormData);
    setSelectedUser(null);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');

    try {
      if (selectedUser) {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await api.put(`/users/${selectedUser.id}`, updateData);
        setSuccess('Usuario actualizado exitosamente');
      } else {
        await api.post('/users', formData);
        setSuccess('Usuario creado exitosamente');
      }
      handleCloseDialog();
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${selectedUser.id}`);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      setSuccess('Usuario eliminado exitosamente');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar usuario');
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { header: 'Nombre', accessor: 'name' },
    { header: 'Email', accessor: 'email' },
    {
      header: 'Creado',
      accessor: 'createdAt',
      render: (value) => new Date(value).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Usuarios"
        subtitle="Gestiona los usuarios del sistema"
        actionLabel="Nuevo Usuario"
        actionIcon={<PersonAdd />}
        onAction={() => handleOpenDialog()}
        badge={users.length}
        badgeColor="primary"
      />

      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        </Fade>
      )}

      {success && (
        <Fade in>
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Fade>
      )}

      <Box sx={{ mb: 3 }}>
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por nombre o email..."
          sx={{ minWidth: 300 }}
        />
      </Box>

      <DataTable
        columns={columns}
        data={filteredUsers}
        loading={loading}
        onEdit={handleOpenDialog}
        onDelete={(user) => {
          setSelectedUser(user);
          setDeleteDialogOpen(true);
        }}
        emptyMessage="No hay usuarios registrados"
        emptyDescription="Crea tu primer usuario para comenzar"
        emptyActionLabel="Crear Usuario"
        emptyAction={() => handleOpenDialog()}
      />

      <FormDialog
        open={dialogOpen}
        title={selectedUser ? 'Editar Usuario' : 'Nuevo Usuario'}
        subtitle={selectedUser ? 'Modifica los datos del usuario' : 'Ingresa los datos del nuevo usuario'}
        icon={<People />}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        loading={saving}
      >
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <TextField
              label="Nombre"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              autoFocus
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={selectedUser ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña'}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              fullWidth
              required={!selectedUser}
              helperText={selectedUser ? 'Solo completa si deseas cambiar la contraseña' : ''}
            />
          </Grid>
        </Grid>
      </FormDialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Eliminar Usuario"
        message={`¿Estás seguro de que deseas eliminar al usuario "${selectedUser?.name}"? Esta acción no se puede deshacer.`}
        type="delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default UsersPage;
